import type { PlayerState, StatKey } from '@core/types';
import { STAGE_LABEL, netWorth, runwayMonths, socialHealth } from '@core/derived';
import './Hud.css';

const STAT_META: { key: StatKey; label: string; short: string; invert?: boolean }[] = [
  { key: 'health', label: 'Health', short: 'HEA' },
  { key: 'energy', label: 'Energy', short: 'ENE' },
  { key: 'stress', label: 'Stress', short: 'STR', invert: true },
  { key: 'happiness', label: 'Happiness', short: 'HAP' },
  { key: 'purpose', label: 'Purpose', short: 'PUR' },
];

export function money(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}k`;
  return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
}

const BURNOUT_LABEL: Record<string, string> = {
  healthy: 'Steady',
  overextended: 'Overextended',
  exhausted: 'Exhausted',
  atRisk: 'Running on empty',
  burntOut: 'Burnt out',
  recovering: 'Recovering',
  rebuilding: 'Rebuilding',
};

interface Props {
  state: PlayerState;
  showNumbers: boolean;
  onOpenPanel: (panel: 'character' | 'career' | 'finance' | 'relationships' | 'goals' | 'timeline' | 'settings') => void;
}

export function Hud({ state, showNumbers, onOpenPanel }: Props) {
  // Computed on every render rather than memoised: these are cheap, and
  // memoising on a mutated object silently froze them for the whole game.
  const summary = {
    nw: netWorth(state),
    runway: runwayMonths(state),
    social: Math.round(socialHealth(state)),
  };

  const careerLine =
    state.career.status === 'employed' || state.career.status === 'selfEmployed'
      ? `${state.career.fieldId ?? 'Working'} · level ${state.career.roleLevel + 1}`
      : state.career.status === 'student'
        ? 'Studying'
        : state.career.status === 'founder'
          ? state.business.name || 'Running a business'
          : state.career.status === 'retired'
            ? 'Retired'
            : state.career.status === 'break'
              ? 'On a break'
              : state.career.status === 'unemployed'
                ? 'Between jobs'
                : 'Just starting';

  const warning =
    state.flags['hardship']
      ? 'Money is not reaching the end of the year'
      : state.burnout.stage === 'burntOut'
        ? 'You have stopped'
        : state.burnout.stage === 'atRisk'
          ? 'Running on empty'
          : summary.runway < 2 && (state.flags['everEmployed'] || state.age >= 24)
            ? 'Almost no runway left'
            : null;

  return (
    <header className="hud">
      <button type="button" className="hud__identity" onClick={() => onOpenPanel('character')}>
        <span className="hud__age numeral">{state.age}</span>
        <span className="hud__identityText">
          <span className="hud__name">{state.identity.name}</span>
          <span className="hud__stage">{STAGE_LABEL[state.stage]}</span>
        </span>
      </button>

      <div className="hud__stats" role="group" aria-label="Wellbeing">
        {STAT_META.map((meta) => {
          const value = Math.round(state.stats[meta.key]);
          const good = meta.invert ? value <= 45 : value >= 55;
          const bad = meta.invert ? value >= 70 : value <= 30;
          return (
            <div
              key={meta.key}
              className={`hud__stat${good ? ' hud__stat--good' : ''}${bad ? ' hud__stat--bad' : ''}`}
              title={`${meta.label}: ${value}`}
            >
              <span className="hud__statLabel">{meta.short}</span>
              <span className="hud__meter" aria-hidden="true">
                <span
                  className="hud__meterFill"
                  style={{ width: `${value}%`, background: `var(--c-${meta.key})` }}
                />
              </span>
              {showNumbers && <span className="hud__statValue numeral">{value}</span>}
              <span className="sr-only">
                {meta.label} {value} out of 100
              </span>
            </div>
          );
        })}
      </div>

      <nav className="hud__nav" aria-label="Life details">
        <button type="button" className="hud__chip" onClick={() => onOpenPanel('career')}>
          <span className="hud__chipLabel">Work</span>
          <span className="hud__chipValue">{careerLine}</span>
        </button>
        <button type="button" className="hud__chip" onClick={() => onOpenPanel('finance')}>
          <span className="hud__chipLabel">Net worth</span>
          <span className="hud__chipValue numeral">{money(summary.nw)}</span>
        </button>
        <button type="button" className="hud__chip" onClick={() => onOpenPanel('relationships')}>
          <span className="hud__chipLabel">People</span>
          <span className="hud__chipValue">{describeSocial(summary.social)}</span>
        </button>
        <button type="button" className="hud__chip hud__chip--slim" onClick={() => onOpenPanel('goals')}>
          <span className="hud__chipLabel">Goals</span>
          <span className="hud__chipValue">
            {state.aspirations.filter((a) => a.status === 'active').length}
          </span>
        </button>
        <button type="button" className="hud__chip hud__chip--slim" onClick={() => onOpenPanel('timeline')}>
          <span className="hud__chipLabel">Story</span>
          <span className="hud__chipValue">{state.log.length}</span>
        </button>
        <button
          type="button"
          className="hud__icon"
          onClick={() => onOpenPanel('settings')}
          aria-label="Settings and saves"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M12 3v2.2M12 18.8V21M3 12h2.2M18.8 12H21M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </nav>

      {(warning || state.burnout.stage !== 'healthy') && (
        <div className={`hud__alert${warning ? ' hud__alert--warn' : ''}`} role="status">
          {warning ?? BURNOUT_LABEL[state.burnout.stage]}
        </div>
      )}
    </header>
  );
}

function describeSocial(score: number): string {
  if (score >= 72) return 'Close';
  if (score >= 55) return 'Steady';
  if (score >= 38) return 'Thinning';
  return 'Distant';
}
